import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import {
    TradeHistory,
    TradeHistoryFilters,
    TradeHistorySummary,
    TradeHistoryCreateInput,
    TradeHistoryUpdateInput,
} from '../../models/trade-history';
import { API_ENDPOINTS } from '../endpoints';
import { PaginatedResponse } from '../types';
import { useSnackbar } from 'notistack';

// List trade history with pagination and filtering
const fetchTradeHistory = async (filters: TradeHistoryFilters = {}) => {
    const { data } = await httpClient.get<PaginatedResponse<TradeHistory>>(
        API_ENDPOINTS.CRM.TRADE_HISTORY.LIST,
        { params: filters }
    );
    return data;
};

// Fetch trade history summary/statistics
const fetchTradeHistorySummary = async (filters: TradeHistoryFilters = {}) => {
    const { data } = await httpClient.get<ApiResponse<TradeHistorySummary>>(
        API_ENDPOINTS.CRM.TRADE_HISTORY.STATISTICS,
        { params: filters }
    );
    return data.data;
};

export const useTradeHistory = (filters: TradeHistoryFilters = {}) => {
    return useQuery<PaginatedResponse<TradeHistory>, Error>({
        queryKey: [API_ENDPOINTS.CRM.TRADE_HISTORY.LIST, filters],
        queryFn: () => fetchTradeHistory(filters),
    });
};

// Hook for trade history summary/statistics
export const useTradeHistorySummary = (filters: TradeHistoryFilters = {}) => {
    return useQuery<TradeHistorySummary, Error>({
        queryKey: [API_ENDPOINTS.CRM.TRADE_HISTORY.STATISTICS, filters],
        queryFn: () => fetchTradeHistorySummary(filters),
    });
};

// Get trade history by ID
const fetchTradeHistoryItem = async (id: string) => {
    const { data } = await httpClient.get<ApiResponse<TradeHistory>>(
        API_ENDPOINTS.CRM.TRADE_HISTORY.DETAILS(id)
    );
    return data.data;
};

export const useTradeHistoryItem = (id: string) => {
    return useQuery<TradeHistory, Error>({
        queryKey: [API_ENDPOINTS.CRM.TRADE_HISTORY.LIST, id],
        queryFn: () => fetchTradeHistoryItem(id),
        enabled: !!id,
    });
};

// Update trade history hook for inline editing
export const useUpdateTradeHistory = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async ({
            tradeHistoryId,
            data,
        }: {
            tradeHistoryId: string;
            data: TradeHistoryUpdateInput;
        }) => {
            const { data: response } = await httpClient.put<ApiResponse>(
                API_ENDPOINTS.CRM.TRADE_HISTORY.DETAILS(tradeHistoryId),
                data
            );
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.TRADE_HISTORY.LIST] });
            queryClient.invalidateQueries({
                queryKey: [API_ENDPOINTS.CRM.TRADE_HISTORY.STATISTICS],
            });
            enqueueSnackbar('Trade history updated successfully', { variant: 'success' });
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to update trade history', {
                variant: 'error',
            });
        },
    });
};

// Delete trade history hook
export const useDeleteTradeHistory = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async (tradeHistoryId: string) => {
            const { data: response } = await httpClient.delete<ApiResponse>(
                API_ENDPOINTS.CRM.TRADE_HISTORY.DETAILS(tradeHistoryId)
            );
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.TRADE_HISTORY.LIST] });
            queryClient.invalidateQueries({
                queryKey: [API_ENDPOINTS.CRM.TRADE_HISTORY.STATISTICS],
            });
            enqueueSnackbar('Trade history deleted successfully', { variant: 'success' });
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to delete trade history', {
                variant: 'error',
            });
        },
    });
};

// Reopen trade history as position hook
export const useReopenTradeHistory = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async (tradeHistoryId: string) => {
            const { data: response } = await httpClient.post<ApiResponse>(
                API_ENDPOINTS.CRM.TRADE_HISTORY.REOPEN(tradeHistoryId)
            );
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.TRADE_HISTORY.LIST] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.ORDERS.LIST] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.POSITIONS.LIST] });
            enqueueSnackbar('Trade history reopened as position successfully', {
                variant: 'success',
            });
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to reopen trade history', {
                variant: 'error',
            });
        },
    });
};

// Bulk reopen trade history hook
export const useBulkReopenTradeHistory = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async (tradeHistoryIds: string[]) => {
            const { data: response } = await httpClient.post<ApiResponse>(
                API_ENDPOINTS.CRM.TRADE_HISTORY.BULK_REOPEN,
                { trade_history_ids: tradeHistoryIds }
            );
            return response;
        },
        onSuccess: data => {
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.TRADE_HISTORY.LIST] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.ORDERS.LIST] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.POSITIONS.LIST] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.POSITIONS.LIST] });

            enqueueSnackbar(data.message || 'Bulk reopen completed successfully', {
                variant: 'success',
            });
        },
        onError: (error: any) => {
            enqueueSnackbar(
                error?.response?.data?.message || 'Failed to bulk reopen trade history',
                {
                    variant: 'error',
                }
            );
        },
    });
};

// Export trade history hook
export const useExportTradeHistory = () => {
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async (filters: TradeHistoryFilters = {}) => {
            const response = await httpClient.get(API_ENDPOINTS.CRM.TRADE_HISTORY.EXPORT, {
                params: filters,
                responseType: 'blob',
            });
            return response;
        },
        onSuccess: (response, filters) => {
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                `trade-history-${new Date().toISOString().split('T')[0]}.csv`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            enqueueSnackbar('Trade history exported successfully', { variant: 'success' });
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to export trade history', {
                variant: 'error',
            });
        },
    });
};
